/**
 * MODULE: domain/sales-intelligence/frontend/api/salesIntelRateLimit.ts
 * PURPOSE: Shared gateway **429** backoff for Sales Intelligence axios client and **useInsights** (session + in-memory, **Retry-After** aware).
 * DEPENDENCIES: axios (**isAxiosError**), **`_shared/.../gatewayRateLimit`**
 * PUBLIC_API: bumpSalesIntel429Backoff, isInSalesIntelBackoff, isSalesIntelRateLimited, SALES_INTEL_BACKOFF_SS_KEY
 * SIDE_EFFECTS: **sessionStorage**, module-level deadline
 * AGENT_SAFETY: SAFE
 * FILE_SIZE: 86 / 150 lines
 * RECENT: 2026-05-08 — [2.2.874] **`bumpGateway429FromAxiosError`** inside **`bumpSalesIntel429Backoff`**; **`isInSalesIntelBackoff`** ORs **`isInGatewayBackoff`**.
 */

import { isAxiosError } from 'axios';
import { bumpGateway429FromAxiosError, isInGatewayBackoff } from '../../../../_shared/lib/frontend/api/gatewayRateLimit';

export const SALES_INTEL_BACKOFF_SS_KEY = 'brazn_sales_intel_api_backoff_until';

const MIN_BACKOFF_MS = 60_000;
const MAX_BACKOFF_MS = 15 * 60 * 1000;
/** When gateway omits **Retry-After**, assume a short window (aligns with typical 60s RPM buckets). */
const DEFAULT_BACKOFF_MS = 120_000;

let salesIntelApiBackoffUntil = 0;

export function readStoredSalesIntelBackoff(): number {
  if (typeof sessionStorage === 'undefined') return 0;
  try {
    return parseInt(sessionStorage.getItem(SALES_INTEL_BACKOFF_SS_KEY) || '0', 10);
  } catch {
    return 0;
  }
}

function effectiveSalesIntelBackoffUntil(): number {
  return Math.max(salesIntelApiBackoffUntil, readStoredSalesIntelBackoff());
}

export function isInSalesIntelBackoff(): boolean {
  if (isInGatewayBackoff()) return true;
  return Date.now() < effectiveSalesIntelBackoffUntil();
}

function parse429BackoffMs(err: unknown): number {
  if (!isAxiosError(err) || err.response?.status !== 429) {
    return DEFAULT_BACKOFF_MS;
  }
  const h = err.response.headers as Record<string, string | undefined>;
  const raw = h?.['retry-after'] ?? h?.['Retry-After'];
  if (typeof raw === 'string') {
    const sec = parseInt(raw, 10);
    if (!Number.isNaN(sec) && sec >= 0) {
      return Math.min(Math.max(sec * 1000, MIN_BACKOFF_MS), MAX_BACKOFF_MS);
    }
  }
  const data = err.response?.data;
  if (data && typeof data === 'object' && 'retry_after' in data) {
    const n = (data as { retry_after?: unknown }).retry_after;
    if (typeof n === 'number' && n >= 0) {
      return Math.min(Math.max(n * 1000, MIN_BACKOFF_MS), MAX_BACKOFF_MS);
    }
  }
  return DEFAULT_BACKOFF_MS;
}

/**
 * Arm backoff from a **429** response (or string heuristics). Idempotent for parallel failures: keeps the **latest** deadline.
 * Call from the axios interceptor so the first **429** suppresses further SI HTTP before React Query catch runs.
 */
export function bumpSalesIntel429Backoff(err: unknown): void {
  if (!isAxiosError(err) || err.response?.status !== 429) return;
  bumpGateway429FromAxiosError(err);
  const deltaMs = parse429BackoffMs(err);
  const proposedUntil = Date.now() + deltaMs;
  salesIntelApiBackoffUntil = Math.max(salesIntelApiBackoffUntil, proposedUntil);
  try {
    if (typeof sessionStorage !== 'undefined') {
      sessionStorage.setItem(SALES_INTEL_BACKOFF_SS_KEY, String(salesIntelApiBackoffUntil));
    }
  } catch {
    /* private mode / SSR */
  }
}

export function isSalesIntelRateLimited(err: unknown): boolean {
  if (isAxiosError(err) && err.response?.status === 429) return true;
  const msg = err instanceof Error ? err.message : String(err);
  return /too many requests|429|current usage:\s*\d+/i.test(msg);
}
